import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { format, startOfDay, endOfDay } from 'date-fns';
import { getInTransitShipments, getInTransitAlerts, getInTransitTabs, getCustomerCodes, CustomerOption, getBranches, BranchOption, getGeocodingErrorsCount, TabInfo } from '../api/shipments';
import { DataTable } from 'components/ui/data-table';
import { CustomTabs } from 'components/ui/custom-tabs';
import { TabsSkeleton } from 'components/ui/tabs-skeleton';
import { createColumns } from './columns';
import { useBreadcrumbs } from 'contexts/BreadcrumbsContext';
import { GeocodingErrorsModal } from './GeocodingErrorsModal';

interface DateRange {
  from?: Date;
  to?: Date;
}

interface PaginationState {
  pageIndex: number;
  pageSize: number;
}

interface SortingState {
  id: string;
  desc: boolean
}

interface AlertFilter {
  discrepancy_type?: string;
  untracked?: string;
  arrival?: string;
  cust_id?: string;
}

interface AlertBucket {
  label: string;
  count: number;
  filters: AlertFilter;
}

interface AlertCard {
  id: string;
  title: string;
  type: 'bucketed' | 'single';
  style: 'positive' | 'negative' | 'neutral' | 'total';
  count?: number;
  filters?: AlertFilter;
  buckets?: AlertBucket[];
}


// Helper to safely parse dates from URL (simple YYYY-MM-DD format)
const safeParseDate = (dateString: string | null): Date | undefined => {
  if (dateString) {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      // Set to start of day for accurate representation when reading from URL
      return startOfDay(date);
    }
  }
  return undefined;
};

// Helper to initialize Alert Filters from URL
const initializeAlertFilters = (params: URLSearchParams): AlertFilter => {
  const filters: AlertFilter = {};
  const discType = params.get('disc_type');
  if (discType) {
    filters.discrepancy_type = discType;
  }
  const untracked = params.get('untracked');
  if (untracked) {
    filters.untracked = untracked;
  }
  const arrival = params.get('arrival');
  if (arrival) {
    filters.arrival = arrival;
  }
  return filters;
};

const InTransitShipmentsPage: React.FC = () => {
  const { setBreadcrumbs } = useBreadcrumbs();
  const [shipments, setShipments] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [isLoadingAlerts, setIsLoadingAlerts] = useState(false);
  const [isLoadingTabs, setIsLoadingTabs] = useState(false);

  const [customerOptions, setCustomerOptions] = useState<CustomerOption[]>([]);
  const [branchOptions, setBranchOptions] = useState<BranchOption[]>([]);
  const [alertCards, setAlertCards] = useState<AlertCard[]>([]);
  const [pageCount, setPageCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [searchDebounceTimer, setSearchDebounceTimer] = useState<NodeJS.Timeout | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Tabs state
  const [tabs, setTabs] = useState<TabInfo[]>([]);
  const [activeTab, setActiveTab] = useState<string>(searchParams.get('load_type') || 'FTL');

  // Geocoding errors state
  const [geocodingErrorsCount, setGeocodingErrorsCount] = useState(0);
  const [showGeocodingErrorsModal, setShowGeocodingErrorsModal] = useState(false);

  // Shipment status filter
  const [shipmentStatus, setShipmentStatus] = useState<string | undefined>(searchParams.get('shipment_status') || undefined);


  // Pagination
  const initialPageIndex = parseInt(searchParams.get('page') || '1', 10) - 1;
  const initialPageSize = parseInt(searchParams.get('per_page') || '20', 10);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: Math.max(0, initialPageIndex),
    pageSize: initialPageSize > 0 ? initialPageSize : 20,
  });

  // Sorting
  const initialSortId = searchParams.get('sort_by') || 'cn_date';
  const initialSortOrder = searchParams.get('sort_order') === 'desc';
  const [sorting, setSorting] = useState<SortingState[]>([{ id: initialSortId, desc: initialSortOrder }]);

  // Global Filter (Search)
  const [globalFilter, setGlobalFilter] = useState(searchParams.get('search') || '');

  // Customer Filter
  const [selectedCustId, setSelectedCustId] = useState<string | undefined>(searchParams.get('customer') || undefined);

  // Branch Filter
  const [selectedOuCode, setSelectedOuCode] = useState<string | undefined>(searchParams.get('branch') || undefined);

  // Date Range Filter
  const [dateRange, setDateRange] = useState<DateRange>({
    from: safeParseDate(searchParams.get('from_date')),
    to: safeParseDate(searchParams.get('to_date'))
  });

  // Alert Filters
  const [activeAlertFilters, setActiveAlertFilters] = useState<AlertFilter>(() => initializeAlertFilters(searchParams));

  // Set breadcrumbs
  useEffect(() => {
    setBreadcrumbs([
      { label: 'Shipments', href: '#' },
      { label: 'In Transit' }
    ]);
  }, [setBreadcrumbs]);

  useEffect(() => {
    // This effect runs whenever a state changes and writes ALL current state to the URL.
    const next = new URLSearchParams();

    // 1. Pagination (pageIndex is 0-based, URL 'page' is 1-based)
    next.set('page', String(pagination.pageIndex + 1));
    next.set('per_page', String(pagination.pageSize));

    // 2. Sorting
    if (sorting.length > 0) {
      next.set('sort_by', sorting[0].id);
      next.set('sort_order', sorting[0].desc ? 'desc' : 'asc');
    }

    // 3. Global Filter (Search)
    if (globalFilter) {
      next.set('search', globalFilter);
    }

    // 4. Customer Filter
    if (selectedCustId) {
      next.set('customer', selectedCustId);
    }

    // 4.5. Branch Filter
    if (selectedOuCode) {
      next.set('branch', selectedOuCode);
    }

    // 5. Date Range Filter
    if (dateRange.from) {
      // Store as simple date string for URL
      next.set('from_date', format(dateRange.from, 'yyyy-MM-dd'));
    }
    if (dateRange.to) {
      // Store as simple date string for URL
      next.set('to_date', format(dateRange.to, 'yyyy-MM-dd'));
    }

    // 6. Active Alert Filters
    if (activeAlertFilters.discrepancy_type) {
      next.set('disc_type', activeAlertFilters.discrepancy_type);
    }
    if (activeAlertFilters.untracked) {
      next.set('untracked', activeAlertFilters.untracked);
    }
    if (activeAlertFilters.arrival) {
      next.set('arrival', activeAlertFilters.arrival);
    }

    // 7. Active Tab (Load Type)
    if (activeTab && activeTab !== 'FTL') {
      next.set('load_type', activeTab);
    }

    // 8. Shipment Status Filter
    if (shipmentStatus) {
      next.set('shipment_status', shipmentStatus);
    }

    // Update the URL without adding a new entry to the browser history
    setSearchParams(next, { replace: true });

  }, [
    pagination.pageIndex, pagination.pageSize,
    globalFilter,
    selectedCustId,
    selectedOuCode,
    dateRange.from, dateRange.to,
    activeAlertFilters.discrepancy_type, activeAlertFilters.untracked, activeAlertFilters.arrival,
    sorting[0]?.id, sorting[0]?.desc,
    activeTab,
    shipmentStatus,
    setSearchParams
  ]);

  const fetchCustomerData = useCallback(async () => {
    try {
      const params: any = {};

      if (globalFilter) {
        params.search = globalFilter;
      }
      if (dateRange.from) {
        params.from = format(startOfDay(dateRange.from), 'yyyy-MM-dd HH:mm:ss');
      }
      if (dateRange.to) {
        params.to = format(endOfDay(dateRange.to), 'yyyy-MM-dd HH:mm:ss');
      }

      params.status = "in_transit";

      const response = await getCustomerCodes(params);

      if (response && response.length > 0) {
        const options: CustomerOption[] = response
          .filter(item => item?.cust_code)
          .map(item => ({
            cust_code: item.cust_code,
            cust_name: item.cust_name || item.cust_code,
          }))
          .sort((a, b) => a.cust_name.localeCompare(b.cust_name));

        setCustomerOptions(options);
      } else {
        setCustomerOptions([]);
      }

    } catch (error) {
      console.error("Error fetching customer data for dropdown:", error);
      setCustomerOptions([]);
    }
  }, [globalFilter, dateRange]);

  const fetchBranchData = useCallback(async () => {
    try {
      const branchParams: any = {};

      if (globalFilter) {
        branchParams.search = globalFilter;
      }
      if (dateRange.from) {
        branchParams.from = format(startOfDay(dateRange.from), 'yyyy-MM-dd HH:mm:ss');
      }
      if (dateRange.to) {
        branchParams.to = format(endOfDay(dateRange.to), 'yyyy-MM-dd HH:mm:ss');
      }

      branchParams.status = "in_transit";

      const branches = await getBranches(branchParams);
      setBranchOptions(branches.sort((a, b) => a.branch_name.localeCompare(b.branch_name)));

    } catch (error) {
      console.error('Error fetching branch data for dropdown:', error);
      setBranchOptions([]);
    }
  }, [globalFilter, dateRange]);

  // Function to fetch geocoding errors count
  const fetchGeocodingErrorsCount = useCallback(async () => {
    try {
      const response = await getGeocodingErrorsCount();
      setGeocodingErrorsCount(response.count);
    } catch (error) {
      console.error('Error fetching geocoding errors count:', error);
      setGeocodingErrorsCount(0);
    }
  }, []);

  // Function to fetch tabs
  const fetchTabs = useCallback(async () => {
    setIsLoadingTabs(true);
    try {
      const params: any = {};

      if (globalFilter) {
        params.search = globalFilter;
      }
      if (dateRange.from) {
        params.from = format(startOfDay(dateRange.from), 'yyyy-MM-dd HH:mm:ss');
      }
      if (dateRange.to) {
        params.to = format(endOfDay(dateRange.to), 'yyyy-MM-dd HH:mm:ss');
      }
      if (selectedCustId) {
        params.cust_id = selectedCustId;
      }
      if (selectedOuCode) {
        params.ou_code = selectedOuCode;
      }
      if (activeAlertFilters.discrepancy_type) {
        params.discrepancy_type = activeAlertFilters.discrepancy_type;
      }
      if (activeAlertFilters.untracked) {
        params.untracked = activeAlertFilters.untracked;
      }
      if (activeAlertFilters.arrival) {
        params.arrival = activeAlertFilters.arrival;
      }

      const response = await getInTransitTabs(params);
      setTabs(response.tabs || []);
    } catch (error) {
      console.error('Error fetching tabs:', error);
      setTabs([]);
    } finally {
      setIsLoadingTabs(false);
    }
  }, [globalFilter, dateRange, selectedCustId, selectedOuCode, activeAlertFilters]);

  // Function to fetch alert cards
  const fetchAlerts = useCallback(async () => {
    setIsLoadingAlerts(true);
    try {
      const params: any = {};

      if (globalFilter) {
        params["search"] = globalFilter;
      }
      if (dateRange.from) {
        params["from"] = format(startOfDay(dateRange.from), 'yyyy-MM-dd HH:mm:ss');
      }
      if (dateRange.to) {
        params["to"] = format(endOfDay(dateRange.to), 'yyyy-MM-dd HH:mm:ss');
      }
      if (selectedCustId) {
        params.cust_id = selectedCustId;
      }
      if (activeTab) {
        params.load_type = activeTab;
      }

      const response = await getInTransitAlerts(params);
      setAlertCards(response.cards || []);
    } catch (error) {
      console.error('Error fetching alerts:', error);
      setAlertCards([]);
    } finally {
      setIsLoadingAlerts(false);
    }
  }, [globalFilter, dateRange, selectedCustId, activeTab]);

  const fetchShipments = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {
        page: pagination.pageIndex + 1,
        per_page: pagination.pageSize,
        search: globalFilter,
        sort_by: sorting[0]?.id || 'cn_date',
        sort_order: sorting[0]?.desc ? 'desc' : 'asc',
      };

      // Add date range to params if provided (local time, start/end of day)
      if (dateRange.from) {
        params.from = format(startOfDay(dateRange.from), 'yyyy-MM-dd HH:mm:ss');
      }
      if (dateRange.to) {
        params.to = format(endOfDay(dateRange.to), 'yyyy-MM-dd HH:mm:ss');
      }
      if (activeAlertFilters.discrepancy_type) {
        params.discrepancy_type = activeAlertFilters.discrepancy_type;
      }
      if (activeAlertFilters.untracked) {
        params.untracked = activeAlertFilters.untracked;
      }
      if (activeAlertFilters.arrival) {
        params.arrival = activeAlertFilters.arrival;
      }
      if (selectedCustId) {
        params.cust_id = selectedCustId;
      }
      if (selectedOuCode) {
        params.ou_code = selectedOuCode;
      }
      if (activeTab) {
        params.load_type = activeTab;
      }
      if (shipmentStatus) {
        params.shipment_status = shipmentStatus;
      }

      const response = await getInTransitShipments(params);

      setShipments(response.items || []);
      setTotalCount(response.total_items || 0);
      setPageCount(response.total_pages || 0);
    } catch (error) {
      console.error('Error fetching shipments:', error);
      setShipments([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [pagination.pageIndex, pagination.pageSize, globalFilter, sorting, dateRange, activeAlertFilters, selectedCustId, selectedOuCode, activeTab, shipmentStatus]);

  // Debounced search effect - only reset pagination when search actually changes
  const previousGlobalFilter = useRef('');
  useEffect(() => {
    // Only reset pagination if the search term actually changed (not just initialization)
    if (previousGlobalFilter.current === globalFilter) {
      return;
    }

    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer);
    }

    const timer = setTimeout(() => {
      setPagination(prev => ({ ...prev, pageIndex: 0 }));
    }, 300);

    setSearchDebounceTimer(timer);
    previousGlobalFilter.current = globalFilter;

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [globalFilter, searchDebounceTimer]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  useEffect(() => {
    fetchCustomerData();
  }, [fetchCustomerData]);

  // Fetch geocoding errors count on mount
  useEffect(() => {
    fetchGeocodingErrorsCount();
  }, [fetchGeocodingErrorsCount]);

  // Fetch branch data when dependencies change
  useEffect(() => {
    fetchBranchData();
  }, [fetchBranchData]);

  // Fetch tabs when dependencies change
  useEffect(() => {
    fetchTabs();
  }, [fetchTabs]);

  useEffect(() => {
    fetchShipments();
  }, [fetchShipments]);


  const handlePaginationChange = (updater: any) => {
    setPagination(updater);
  };

  const handleSortingChange = (updater: any) => {
    setSorting(updater);
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  const handleGlobalFilterChange = (value: string) => {
    setGlobalFilter(value);
  };

  const handleDateRangeChange = (newDateRange: DateRange) => {
    setDateRange(newDateRange || { from: undefined, to: undefined });
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  const handleAlertFilterChange = (filters: AlertFilter) => {
    setActiveAlertFilters(filters);
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  const handleCustomerChange = (code: string | undefined) => {
    setSelectedCustId(code);
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  const handleBranchChange = (ouCode: string | undefined) => {
    setSelectedOuCode(ouCode);
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  const handleGeocodingErrorsSuccess = () => {
    fetchGeocodingErrorsCount();
  };

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  const handleShipmentStatusChange = (value: string | undefined) => {
    setShipmentStatus(value);
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  const columnsWithRefresh = createColumns(fetchShipments, fetchAlerts);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Custom Tabs Navigation */}
      <div className="shrink-0 ml-4 mr-4">
        {isLoadingTabs ? (
          <TabsSkeleton count={3} />
        ) : tabs.length > 0 ? (
          <CustomTabs
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            disabled={loading || isLoadingTabs}
          />
        ) : null}
      </div>

      {/* Tab Content */}
      <div className="flex-1 min-h-0">
        <DataTable
          columns={columnsWithRefresh}
          data={shipments}
          loading={loading}
          pagination={pagination}
          onPaginationChange={handlePaginationChange}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          globalFilter={globalFilter}
          onGlobalFilterChange={handleGlobalFilterChange}
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
          totalCount={totalCount}
          pageCount={pageCount}
          alertCards={alertCards}
          activeAlertFilters={activeAlertFilters}
          onAlertFilterChange={handleAlertFilterChange}
          customerOptions={customerOptions}
          selectedCustId={selectedCustId}
          onCustomerChange={handleCustomerChange}
          branchOptions={branchOptions}
          selectedOuCode={selectedOuCode}
          onBranchChange={handleBranchChange}
          isLoadingAlerts={isLoadingAlerts}
          alertSkeletonCount={3}
          shipmentStatus={shipmentStatus}
          onShipmentStatusChange={handleShipmentStatusChange}
          geocodingErrorsCount={geocodingErrorsCount}
          onGeocodingErrorsClick={() => setShowGeocodingErrorsModal(true)}
        />
      </div>

      <GeocodingErrorsModal
        isOpen={showGeocodingErrorsModal}
        onClose={() => setShowGeocodingErrorsModal(false)}
        onSuccess={handleGeocodingErrorsSuccess}
      />
    </div>
  );
};

export default InTransitShipmentsPage;
